import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Plus, Percent, DollarSign, Trash2, Pencil, X, Tag, Calendar, ShoppingBag, Hash, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDiscounts() {
    const [discounts, setDiscounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [listings, setListings] = useState([]);
    const [form, setForm] = useState({
        code: '', discount_type: 'percentage', discount_value: '',
        max_uses: '', min_order_amount: '', expires_at: '', applicable_listings: []
    });
    const [saving, setSaving] = useState(false);

    const fetchDiscounts = useCallback(async () => {
        try {
            const params = filter !== 'all' ? { status: filter } : {};
            const res = await axios.get(`${API}/api/admin/discounts`, { params, withCredentials: true });
            setDiscounts(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [filter]);

    const fetchListings = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/admin/listings`, { withCredentials: true });
            setListings(res.data.listings || res.data || []);
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => { fetchDiscounts(); }, [fetchDiscounts]);
    useEffect(() => { fetchListings(); }, [fetchListings]);

    const resetForm = () => {
        setForm({ code: '', discount_type: 'percentage', discount_value: '', max_uses: '', min_order_amount: '', expires_at: '', applicable_listings: [] });
        setEditingId(null);
        setShowForm(false);
    };

    const handleEdit = (d) => {
        setForm({
            code: d.code, discount_type: d.discount_type, discount_value: String(d.discount_value),
            max_uses: d.max_uses ? String(d.max_uses) : '', min_order_amount: d.min_order_amount ? String(d.min_order_amount) : '',
            expires_at: d.expires_at ? d.expires_at.split('T')[0] : '', applicable_listings: d.applicable_listings || []
        });
        setEditingId(d.id);
        setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.code.trim() || !form.discount_value) { toast.error('Code and discount value are required'); return; }
        setSaving(true);
        try {
            const payload = {
                code: form.code.trim().toUpperCase(),
                discount_type: form.discount_type,
                discount_value: parseFloat(form.discount_value),
                max_uses: form.max_uses ? parseInt(form.max_uses) : 0,
                min_order_amount: form.min_order_amount ? parseFloat(form.min_order_amount) : 0,
                expires_at: form.expires_at ? new Date(form.expires_at + 'T23:59:59Z').toISOString() : null,
                applicable_listings: form.applicable_listings
            };
            if (editingId) {
                await axios.put(`${API}/api/admin/discounts/${editingId}`, payload, { withCredentials: true });
                toast.success('Discount updated');
            } else {
                await axios.post(`${API}/api/admin/discounts`, payload, { withCredentials: true });
                toast.success('Discount created');
            }
            resetForm();
            fetchDiscounts();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this discount code?')) return;
        try {
            await axios.delete(`${API}/api/admin/discounts/${id}`, { withCredentials: true });
            toast.success('Deleted');
            fetchDiscounts();
        } catch (e) { toast.error('Failed to delete'); }
    };

    const toggleActive = async (d) => {
        try {
            await axios.put(`${API}/api/admin/discounts/${d.id}`, { is_active: !d.is_active }, { withCredentials: true });
            fetchDiscounts();
        } catch (e) { toast.error('Failed to update'); }
    };

    const toggleListing = (listingId) => {
        setForm(prev => {
            const current = prev.applicable_listings || [];
            return { ...prev, applicable_listings: current.includes(listingId) ? current.filter(id => id !== listingId) : [...current, listingId] };
        });
    };

    if (loading) return <DashboardLayout type="admin"><div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div></DashboardLayout>;

    return (
        <DashboardLayout type="admin">
        <div className="space-y-6" data-testid="admin-discounts">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white">Discount Codes</h1>
                    <p className="text-sm text-muted-foreground mt-1">Create and manage promotional discount codes</p>
                </div>
                <div className="flex gap-2">
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-[130px] bg-white/5 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Codes</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="create-discount-btn">
                        <Plus className="w-4 h-4" /> New Code
                    </Button>
                </div>
            </div>

            {/* Create/Edit Form */}
            {showForm && (
                <Card className="bg-[#121620] border-white/10" data-testid="discount-form">
                    <CardContent className="p-5 sm:p-6 space-y-5">
                        <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-white">{editingId ? 'Edit Discount Code' : 'Create New Discount Code'}</h3>
                            <button onClick={resetForm} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <Label className="text-white/60 text-xs mb-1.5 block">Code *</Label>
                                <Input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                                    placeholder="e.g. SUMMER20" className="bg-white/5 border-white/10 text-white uppercase" data-testid="discount-code-input" />
                            </div>
                            <div>
                                <Label className="text-white/60 text-xs mb-1.5 block">Discount Type *</Label>
                                <Select value={form.discount_type} onValueChange={v => setForm(p => ({ ...p, discount_type: v }))}>
                                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="discount-type-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-white/60 text-xs mb-1.5 block">Discount Value *</Label>
                                <div className="relative">
                                    <Input type="number" value={form.discount_value} onChange={e => setForm(p => ({ ...p, discount_value: e.target.value }))}
                                        placeholder={form.discount_type === 'percentage' ? 'e.g. 20' : 'e.g. 5.00'}
                                        className="bg-white/5 border-white/10 text-white pr-8" data-testid="discount-value-input" />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 text-sm">
                                        {form.discount_type === 'percentage' ? '%' : '€'}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <Label className="text-white/60 text-xs mb-1.5 block">Max Uses (0 = unlimited)</Label>
                                <Input type="number" value={form.max_uses} onChange={e => setForm(p => ({ ...p, max_uses: e.target.value }))}
                                    placeholder="0" className="bg-white/5 border-white/10 text-white" data-testid="discount-max-uses" />
                            </div>
                            <div>
                                <Label className="text-white/60 text-xs mb-1.5 block">Min Order Amount (optional)</Label>
                                <Input type="number" value={form.min_order_amount} onChange={e => setForm(p => ({ ...p, min_order_amount: e.target.value }))}
                                    placeholder="0" className="bg-white/5 border-white/10 text-white" data-testid="discount-min-order" />
                            </div>
                            <div>
                                <Label className="text-white/60 text-xs mb-1.5 block">Expiry Date (optional)</Label>
                                <Input type="date" value={form.expires_at} onChange={e => setForm(p => ({ ...p, expires_at: e.target.value }))}
                                    className="bg-white/5 border-white/10 text-white" data-testid="discount-expiry" />
                            </div>
                        </div>

                        {/* Listing Scope */}
                        <div>
                            <Label className="text-white/60 text-xs mb-2 block">Applicable Listings (leave empty for all listings)</Label>
                            <div className="flex flex-wrap gap-2">
                                {listings.map(l => (
                                    <button key={l.id} onClick={() => toggleListing(l.id)}
                                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                                            (form.applicable_listings || []).includes(l.id)
                                                ? 'bg-gold/15 text-gold border-gold/30'
                                                : 'bg-white/5 text-white/50 border-white/10 hover:border-white/20'
                                        }`}>
                                        {l.title}
                                    </button>
                                ))}
                                {listings.length === 0 && <span className="text-white/30 text-xs">No listings found. Code applies to all.</span>}
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <Button variant="outline" onClick={resetForm} className="border-white/10 text-white/60">Cancel</Button>
                            <Button onClick={handleSave} disabled={saving} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="save-discount-btn">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                {editingId ? 'Update' : 'Create'} Discount
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Discounts Table */}
            <Card className="bg-[#0D1017] border-white/10 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-0" data-testid="discounts-table">
                        <thead>
                            <tr className="border-b border-white/10 text-left">
                                <th className="px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Code</th>
                                <th className="px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Discount</th>
                                <th className="px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Usage</th>
                                <th className="px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Min Order</th>
                                <th className="px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Scope</th>
                                <th className="px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Expires</th>
                                <th className="px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Active</th>
                                <th className="px-3 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {discounts.map(d => (
                                <tr key={d.id} className="border-b border-white/5 hover:bg-white/2" data-testid={`discount-row-${d.id}`}>
                                    <td className="px-3 py-3">
                                        <div className="flex items-center gap-2">
                                            <Tag className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                                            <span className="text-white font-mono font-semibold text-sm">{d.code}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-3">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                            d.discount_type === 'percentage' ? 'bg-blue-500/15 text-blue-400' : 'bg-emerald-500/15 text-emerald-400'
                                        }`}>
                                            {d.discount_type === 'percentage' ? <Percent className="w-3 h-3" /> : <DollarSign className="w-3 h-3" />}
                                            {d.discount_value}{d.discount_type === 'percentage' ? '%' : ''}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3">
                                        <span className="text-white/60 text-sm flex items-center gap-1">
                                            <Hash className="w-3 h-3 flex-shrink-0" />
                                            {d.used_count || 0}{d.max_uses > 0 ? `/${d.max_uses}` : ' / Unlim.'}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3 text-white/50 text-sm">
                                        {d.min_order_amount > 0 ? `${d.min_order_amount}` : '-'}
                                    </td>
                                    <td className="px-3 py-3">
                                        <span className="text-white/50 text-xs">
                                            {(d.applicable_listings || []).length > 0
                                                ? <span className="flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> {d.applicable_listings.length}</span>
                                                : 'All'}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3 text-white/50 text-sm">
                                        {d.expires_at ? (
                                            <span className={`flex items-center gap-1 ${new Date(d.expires_at) < new Date() ? 'text-red-400' : ''}`}>
                                                <Calendar className="w-3 h-3 flex-shrink-0" />
                                                {new Date(d.expires_at).toLocaleDateString()}
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td className="px-3 py-3">
                                        <Switch checked={d.is_active} onCheckedChange={() => toggleActive(d)} data-testid={`toggle-discount-${d.id}`} />
                                    </td>
                                    <td className="px-3 py-3">
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => handleEdit(d)} className="p-1.5 rounded hover:bg-white/5 text-white/40 hover:text-white" data-testid={`edit-discount-${d.id}`}>
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => handleDelete(d.id)} className="p-1.5 rounded hover:bg-red-500/10 text-white/40 hover:text-red-400" data-testid={`delete-discount-${d.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {discounts.length === 0 && (
                        <div className="py-12 text-center text-white/20">
                            <Tag className="w-8 h-8 mx-auto mb-3 opacity-40" />
                            <p className="text-sm">No discount codes yet</p>
                        </div>
                    )}
                </div>
            </Card>
        </div>
        </DashboardLayout>
    );
}
